import React from 'react';
import { format, Locale } from 'date-fns';
import { Target, Calendar, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import type { Goal } from '@/types/goals';
import { useTranslation } from 'react-i18next';
import { es, enUS, fr } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';

interface GoalCardProps {
  goal: Goal;
  onClick: () => void;
  lastUpdated?: Date;
}

// Componentes extraídos
const GoalHeader: React.FC<{ goal: Goal }> = ({ goal }) => {
  const { t, i18n } = useTranslation();
  const currentLocale = getDateFnsLocale(i18n.language);

  return (
    <div className="flex items-center space-x-3 w-full">
      <div className="p-2 sm:p-3 bg-blue-100 dark:bg-blue-900/50 rounded-full shrink-0">
        <Target className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-300" />
      </div>
      <div className="min-w-0">
        <h3 className="font-semibold text-base sm:text-lg text-gray-900 dark:text-white truncate">{goal.title}</h3>
        <GoalDateRange startDate={goal.startDate} endDate={goal.endDate} locale={currentLocale} />
      </div>
    </div>
  );
};

const GoalDateRange: React.FC<{ startDate: string; endDate: string; locale: Locale }> = ({ startDate, endDate, locale }) => (
  <div className="flex items-center space-x-1 sm:space-x-2 mt-1">
    <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
      {format(new Date(startDate), 'd MMM', { locale })} - {format(new Date(endDate), 'd MMM', { locale })}
    </p>
  </div>
);

const GoalStatus = ({ overdueCount, isCompleted, isOverdue }: { overdueCount: number; isCompleted: boolean; isOverdue: boolean }) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-wrap items-center gap-2">
      {overdueCount > 0 && !isCompleted && (
        <div className="flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-amber-100 dark:bg-amber-900/30 rounded-full text-amber-600 dark:text-amber-300">
          <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
          <span className="text-xs sm:text-sm font-medium">{overdueCount}</span>
        </div>
      )}
      {isOverdue && !isCompleted && (
        <div className="flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-red-100 dark:bg-red-900/30 rounded-full text-red-600 dark:text-red-300">
          <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
          <span className="text-xs sm:text-sm font-medium">{t('goals.overdue')}</span>
        </div>
      )}
      {isCompleted && (
        <div className="flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-green-100 dark:bg-green-900/30 rounded-full">
          <CheckCircle2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 text-green-500 dark:text-green-300" />
          <span className="text-xs sm:text-sm font-medium text-green-600 dark:text-green-300">{t('goals.completed')}</span>
        </div>
      )}
    </div>
  );
};

const GoalDescription: React.FC<{ description: string }> = ({ description }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <div className="relative">
      <motion.div
        initial={false}
        animate={{
          height: isExpanded ? "auto" : "2.5rem"
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className={`text-xs sm:text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/30 p-2 sm:p-3 rounded-lg overflow-hidden`}
      >
        {description}
      </motion.div>
      <AnimatePresence>
        {!isExpanded && description && description.length > 100 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-gray-50 dark:from-gray-700/90 to-transparent pointer-events-none"
          />
        )}
      </AnimatePresence>
      {description && description.length > 100 && (
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={(e) => {
            e.stopPropagation(); // Prevent card click
            setIsExpanded(!isExpanded);
          }}
          className="absolute bottom-0 right-0 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-blue-600 dark:text-blue-400 text-xs rounded m-1 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none transition-colors z-10"
        >
          {isExpanded ? "Ver menos" : "Ver más"}
        </motion.button>
      )}
    </div>
  );
};

const GoalProgressBar: React.FC<{ progress: number }> = ({ progress }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-1 sm:space-y-2">
      <div className="flex justify-between text-xs sm:text-sm">
        <span className="text-gray-600 dark:text-gray-400">{t('goals.progress.label')}</span>
        <span className="font-medium text-gray-900 dark:text-white">
          {progress}%
        </span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 sm:h-2.5 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={`h-2 sm:h-2.5 rounded-full ${progress === 100
            ? 'bg-green-500 dark:bg-green-400'
            : progress >= 80
              ? 'bg-blue-500 dark:bg-blue-400'
              : progress >= 40
                ? 'bg-blue-600 dark:bg-blue-500'
                : 'bg-blue-700 dark:bg-blue-600'
            }`}
        />
      </div>
    </div>
  )
};

const GoalMetrics = ({ completedTasks, totalTasks, daysRemaining }: { completedTasks: number; totalTasks: number; daysRemaining: number }) => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-2 gap-2 sm:gap-3 text-sm mt-auto">
      <div className="flex items-center justify-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
        <div className="text-center">
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{t('goals.tasks.label')}</p>
          <p className="font-medium text-base sm:text-xl text-gray-900 dark:text-white mt-1">
            <span className="text-blue-600 dark:text-blue-400">{completedTasks}</span>
            <span className="text-gray-400 dark:text-gray-500">/</span>
            <span>{totalTasks}</span>
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center p-2 sm:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
        <div className="text-center">
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{t('goals.daysRemaining.label')}</p>
          <p className={`font-medium text-base sm:text-xl mt-1 ${daysRemaining < 0 ? 'text-red-600 dark:text-red-400' :
              daysRemaining < 3 ? 'text-amber-600 dark:text-amber-400' :
                'text-gray-900 dark:text-white'
            }`}>
            {daysRemaining}
          </p>
        </div>
      </div>
    </div>
  )
};

const useGoalTimeframe = (goal: Goal) => {
  const today = new Date();
  const end = new Date(goal.endDate);
  const diffTime = end.getTime() - today.getTime();
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  const isOverdue = daysRemaining < 0;

  return { daysRemaining, isOverdue };
};

const getDateFnsLocale = (language: string) => {
  switch (language) {
    case 'es':
      return es;
    case 'fr':
      return fr;
    default:
      return enUS;
  }
};

export function GoalCard({ goal, onClick, lastUpdated }: GoalCardProps) {
  const { t, i18n } = useTranslation();
  const totalTasks = goal.dailyTasks.total || 0;
  const completedTasks = goal.dailyTasks.completed || 0;
  const progress = goal.stats.progress || 0;
  const overdueCount = goal.stats.overdueCount || 0;

  const currentLocale = getDateFnsLocale(i18n.language);

  const { daysRemaining, isOverdue } = useGoalTimeframe(goal);


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -5, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)" }}
      onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl p-4 sm:p-6 cursor-pointer transition-all duration-300 relative border border-gray-100 dark:border-gray-700 h-full flex flex-col"
    >
      {lastUpdated && (
        <div className="absolute top-3 right-3">
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-3 h-3 rounded-full bg-blue-500"
            title={`${t('goals.lastUpdated')}: ${format(new Date(lastUpdated), 'PPp', { locale: currentLocale })}`}
          />
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start justify-between mb-4 sm:mb-5 gap-3">
        {/* Header section */}
        <GoalHeader goal={goal} />
        <div className="mt-2 sm:mt-0">
          <GoalStatus
            isCompleted={goal.isCompleted}
            overdueCount={overdueCount}
            isOverdue={isOverdue}
          />
        </div>
      </div>

      <div className="space-y-4 flex-grow">
        <GoalDescription description={goal.description} />
        <GoalProgressBar progress={progress} />
        <GoalMetrics
          completedTasks={completedTasks}
          totalTasks={totalTasks}
          daysRemaining={daysRemaining}
        />
      </div>
    </motion.div>
  );
}